import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const GoogleCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleGoogleToken, verify2FA } = useAuth();

  const [phase, setPhase] = useState<'loading' | 'twoFA' | 'error'>('loading');
  const [error, setError] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');

  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const token = searchParams.get('token');
    const requires2FA = searchParams.get('requires2FA');
    const tempTok = searchParams.get('tempToken');
    const errorMsg = searchParams.get('error');

    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
      setPhase('error');
      return;
    }

    if (requires2FA === 'true' && tempTok) {
      setTempToken(decodeURIComponent(tempTok));
      setPhase('twoFA');
      return;
    }

    if (token) {
      handleGoogleToken(decodeURIComponent(token))
        .then(() => navigate('/app', { replace: true }))
        .catch(() => {
          setError('Failed to complete sign-in. Please try again.');
          setPhase('error');
        });
      return;
    }

    setError('No authentication data received.');
    setPhase('error');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFAError('');
    setVerifying(true);
    try {
      await verify2FA(tempToken, code);
      navigate('/app', { replace: true });
    } catch (err: any) {
      setTwoFAError(err.response?.data?.message || 'Invalid code.');
      setVerifying(false);
    }
  };

  const cardSx = {
    p: 4,
    borderRadius: 2,
    boxShadow: '0 20px 50px rgba(7,24,45,0.08)',
    width: '100%',
    maxWidth: 420,
    borderColor: 'rgba(7,24,45,0.08)',
  };

  if (phase === 'loading') {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">Completing sign-in…</Typography>
        </Stack>
      </Box>
    );
  }

  if (phase === 'twoFA') {
    return (
      <Box sx={{ minHeight: 'calc(100vh - 260px)', display: 'grid', placeItems: 'center', px: 2, py: { xs: 5, md: 7 }, bgcolor: '#f5f7fb' }}>
        <Paper variant="outlined" sx={cardSx}>
          <Stack spacing={2} component="form" onSubmit={handle2FA}>
            <Typography variant="h5" fontWeight={700}>Two-factor authentication</Typography>
            <Typography variant="body2" color="text.secondary">
              Enter the 6-digit code from your authenticator app.
            </Typography>
            <TextField
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              required
              fullWidth
              autoFocus
            />
            {twoFAError && <Typography variant="body2" color="error">{twoFAError}</Typography>}
            <Button type="submit" variant="contained" color="secondary" size="large" disabled={verifying || code.length < 6}>
              {verifying ? 'Verifying…' : 'Verify'}
            </Button>
            <Button component={RouterLink} to="/login" variant="text" size="small">Back to login</Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: 'calc(100vh - 260px)', display: 'grid', placeItems: 'center', px: 2, py: { xs: 5, md: 7 }, bgcolor: '#f5f7fb' }}>
      <Paper variant="outlined" sx={cardSx}>
        <Stack spacing={2} alignItems="flex-start">
          <Typography variant="h5" fontWeight={700}>Sign-in failed</Typography>
          <Typography variant="body2" color="error">{error}</Typography>
          <Button component={RouterLink} to="/login" variant="contained" color="secondary">
            Back to login
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default GoogleCallback;
